import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, ArrowRight, Workflow, ShoppingBag, Landmark, HeartPulse, Rocket } from "lucide-react";
import api from "@/lib/api";

const INDUSTRY_ICONS = { Retail: ShoppingBag, Banking: Landmark, Healthcare: HeartPulse, "E-commerce": Rocket };
const INDUSTRY_COLORS = {
  Retail: "from-indigo-500/20 to-indigo-500/0 border-indigo-400/30 text-indigo-300",
  Banking: "from-emerald-500/20 to-emerald-500/0 border-emerald-400/30 text-emerald-300",
  Healthcare: "from-rose-500/20 to-rose-500/0 border-rose-400/30 text-rose-300",
  "E-commerce": "from-purple-500/20 to-purple-500/0 border-purple-400/30 text-purple-300",
};

export default function SimulatorPage({ apiPrefix = "/simulator", detailBasePath = "/simulator", title = "Interactive Data Cloud Simulator", subtitle = "Learn by doing — connect real-world data sources, map fields, resolve identities, build segments, and activate audiences across 4 industry business scenarios, just like inside a real Salesforce Data Cloud org.", accentColor = "indigo", testId = "simulator-page" }) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${apiPrefix}/scenarios`).then((res) => setScenarios(res.data)).finally(() => setLoading(false));
  }, [apiPrefix]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="simulator-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <Workflow className={`w-6 h-6 text-${accentColor}-400`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] text-${accentColor}-400`}>Signature Feature</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{title}</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">{subtitle}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
        {scenarios.map((s, i) => {
          const Icon = INDUSTRY_ICONS[s.industry] || Workflow;
          const locked = s.status === "locked";
          const completed = s.status === "completed";
          return (
            <motion.div
              key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`glass-panel rounded-2xl p-6 relative overflow-hidden bg-gradient-to-br ${INDUSTRY_COLORS[s.industry] || ""} ${locked ? "opacity-60" : ""}`}
              data-testid={`simulator-scenario-card-${s.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                {completed ? (
                  <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400" data-testid={`simulator-completed-badge-${s.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {s.total_score}% Score
                  </span>
                ) : locked ? (
                  <Lock className="w-4 h-4 text-slate-400" />
                ) : null}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider">{s.industry} · {s.difficulty}</span>
              <h2 className="font-heading text-lg font-semibold text-white mt-1">{s.title}</h2>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.description}</p>
              <div className="flex items-center justify-between mt-5">
                <span className="text-xs font-bold text-[#FBBF24]">+{s.xp_reward} XP</span>
                {locked ? (
                  <span className="text-xs text-slate-400 flex items-center gap-1" data-testid={`simulator-locked-${s.id}`}><Lock className="w-3 h-3" /> Locked</span>
                ) : (
                  <Link
                    to={`${detailBasePath}/${s.id}`} data-testid={`simulator-enter-btn-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors"
                  >
                    {completed ? "Replay" : "Enter Simulator"} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
